import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, LogIn, LogOut, Store, ChevronDown, User, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { isSupabaseConfigured } from "@/lib/supabase";
import { useCartCount } from "@/hooks/useCartCount";
import { NotificationCenter } from "./NotificationCenter";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const Header = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const count = useCartCount(user?.id);

  // Check if user is a seller
  const isSeller = user?.user_metadata?.role === 'seller';

  return (
    <header className="w-full border-b border-border/40 bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/95 sticky top-0 z-50">
      {!isSupabaseConfigured && (
        <div className="w-full bg-yellow-50 text-yellow-800 text-xs px-4 py-2 text-center border-b border-yellow-200">
          Supabase keys not fully configured. Public browsing works, but auth is disabled.
        </div>
      )}
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <Link to="/" className="flex items-center gap-2">
            <Store className="h-8 w-8 text-primary" />
            <span className="text-xl font-bold">Bahama Marketplace</span>
          </Link>
          <nav className="hidden lg:flex items-center gap-6 text-sm font-medium">
            <Link to="/products" className="text-foreground/70 hover:text-foreground transition-colors py-2 font-medium">
              All Products
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-foreground/70 hover:text-foreground transition-colors py-2">
                Fashion <ChevronDown className="h-3 w-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-48 shadow-luxury-lg border-border/50">
                <DropdownMenuItem onClick={() => navigate("/category/mens-fashion")} className="py-2.5">
                  Men's Fashion
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/category/womens-fashion")} className="py-2.5">
                  Women's Fashion
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/category/kids-fashion")} className="py-2.5">
                  Kids Fashion
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/category/fashion")} className="py-2.5">
                  All Fashion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Link to="/category/electronics" className="text-foreground/70 hover:text-foreground transition-colors py-2">
              Electronics
            </Link>
            <Link to="/category/jewelry" className="text-foreground/70 hover:text-foreground transition-colors py-2">
              Jewelry
            </Link>
            <Link to="/category/beauty-products" className="text-foreground/70 hover:text-foreground transition-colors py-2">
              Beauty
            </Link>
          </nav>
        </div>
        <div className="flex items-center gap-3">
          {user && <NotificationCenter />}
          <Button variant="ghost" size="icon" onClick={() => navigate("/cart")} aria-label="Cart" className="relative hover:bg-muted/50">
            <ShoppingCart className="h-5 w-5" />
            {user && count > 0 && (
              <span className="absolute -top-1 -right-1 text-[10px] bg-foreground text-background rounded-full px-1.5 py-0.5 min-w-[18px] h-[18px] flex items-center justify-center font-medium">
                {count}
              </span>
            )}
          </Button>
          {user ? (
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => navigate("/orders")} className="hidden sm:inline-flex">
                Orders
              </Button>

              {/* Show seller dashboard button only for sellers */}
              {isSeller && (
                <Button variant="ghost" size="icon" onClick={() => navigate("/seller")} aria-label="Seller Dashboard" className="hover:bg-muted/50">
                  <Store className="h-5 w-5" />
                </Button>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="hover:bg-muted/50">
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-48 shadow-luxury-lg border-border/50">
                  <DropdownMenuItem onClick={() => navigate("/profile")} className="py-2.5">
                    <User className="h-4 w-4 mr-2" />
                    My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/settings")} className="py-2.5">
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </DropdownMenuItem>

                  {/* Show seller-specific options */}
                  {isSeller && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate("/seller")} className="py-2.5">
                        <Store className="h-4 w-4 mr-2" />
                        Seller Dashboard
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={async () => { await signOut(); navigate("/"); }} 
                    className="py-2.5 text-red-600"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => navigate("/login")} className="hidden sm:inline-flex">
                <LogIn className="h-4 w-4 mr-2" /> Sign in
              </Button>
              <Button onClick={() => navigate("/register")} className="font-medium">
                Get started
              </Button>

              {/* Seller-specific dropdown for non-logged-in users */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="font-medium hidden sm:inline-flex">
                    <Store className="h-4 w-4 mr-2" />
                    Sell on Bahama Marketplace
                    <ChevronDown className="h-3 w-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-48 shadow-luxury-lg border-border/50">
                  <DropdownMenuItem onClick={() => navigate("/seller/register")} className="py-2.5">
                    <Store className="h-4 w-4 mr-2" />
                    Become a Seller
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/seller/login")} className="py-2.5">
                    <LogIn className="h-4 w-4 mr-2" />
                    Seller Login
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;